/**
 * The report builder.
 *
 * Loads a report's own template, renders it exactly as the PDF will, and lets a person
 * correct a value in place. That is the shape of the tool ProData already uses, kept on
 * purpose: `rtb_ui`'s `editable-area` swaps a figure for an input, commits on blur,
 * cancels on Escape, and rejects anything that is not a non-negative whole number. Those
 * behaviours are reproduced here because staff have them in their fingers.
 *
 * What is not reproduced is the legacy recalculation. Every edit here becomes an
 * `Override`, and the server cascades it through the declared invariants — so correcting
 * total clicks moves the device split with it, apportioned so the parts still sum to the
 * whole. The legacy `recalculate()` is a chain of per-path special cases, which means a
 * figure nobody wrote a branch for silently fails to cascade.
 */

import * as React from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ApiError, builder, reports,
  type BuilderView, type CampaignReportLookup, type OverrideKind, type SettingsField,
  type ImpressionComparison,
} from '../lib/api';
import { ReportCanvas } from '../components/ReportCanvas';
import { useConfirm } from '../components/ConfirmDialog';

/** Where the popover editor sits, in VIEWPORT coordinates — see EditPopover. */
interface Picked {
  path: string;
  current: string;
  numeric: boolean;
  top: number;
  left: number;
  /** A heatmap cell — edited with a 5-level slider rather than the text popover. */
  heat?: boolean;
  /** The heatmap's busiest-cell value, so a level maps back to a cell value. */
  heatMax?: number;
  /** This cell's current value. */
  heatValue?: number;
}

const KIND_HELP: Record<OverrideKind, string> = {
  DATA_GAP: 'A figure is missing or wrong in the source data. No reason required.',
  CONTRACTUAL: 'A commercial agreement overrides what was measured. A reason is required.',
  PRESENTATION: 'How the figure is shown, not what it is. A reason is required.',
};

/**
 * `/campaigns/:id/report` — the way in from a campaign.
 *
 * A campaign is not a report, so this resolves one and forwards to the canonical
 * `/reports/:reportId`. `replace` keeps the redirect out of history: Back from the builder
 * should return to the campaign, not bounce through the resolver again.
 *
 * The empty state matters more than the redirect. Almost no campaign has a report in this
 * store yet, so "no report" is the ordinary answer, and the screen has to say that rather
 * than "not found" — which reads as "your campaign is missing" and sends someone looking
 * for a problem that is not there.
 */
/** Polls (every 2s) to wait for a queued report before declaring generation stuck — ~2 minutes. */
const POLL_LIMIT = 60;

export function CampaignReportScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [lookup, setLookup] = React.useState<CampaignReportLookup | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  /*
   * `queued` is the state between asking and the report existing.
   *
   * Generation runs on its own worker (D6), so the request returns as soon as the job is
   * accepted and the document appears some time later. Without this state the button would
   * look like it did nothing — which is exactly how a queue feels from a UI that does not
   * model the wait.
   */
  const [queued, setQueued] = React.useState<{ reportId: string; duplicate: boolean } | null>(null);
  const [asking, setAsking] = React.useState(false);
  const [waited, setWaited] = React.useState(0);
  // Give up the poll after ~2 minutes (60 × 2s) and say so, rather than spinning for ever on a job that failed.
  const [timedOut, setTimedOut] = React.useState(false);

  React.useEffect(() => {
    let live = true;
    builder.forCampaign(id)
      .then((r) => {
        if (!live) return;
        if (r.reportId) navigate(`/reports/${encodeURIComponent(r.reportId)}`, { replace: true });
        else setLookup(r);
      })
      .catch((e) => { if (live) setError(e instanceof ApiError ? e.message : String(e)); });
    return () => { live = false; };
  }, [id, navigate]);

  /*
   * Poll for the report the queued job will produce, and forward the moment it lands.
   *
   * Polling rather than pushing because the alternative is a socket for one screen that is
   * open for a few seconds. It stops on its own after two minutes: past that the job has
   * almost certainly failed or no worker is consuming the queue, and a spinner that never
   * ends tells nobody anything.
   */
  React.useEffect(() => {
    if (!queued || timedOut) return;
    let live = true;
    const timer = setInterval(async () => {
      if (!live) return;
      // Stop after ~2 minutes rather than spinning for ever. Past this the job has almost certainly
      // FAILED (generation fails fast — an unbuildable window, a data problem) and no report is coming,
      // so a spinner that never ends misreports a failure as progress. Surface it instead.
      setWaited((w) => {
        if (w + 1 >= POLL_LIMIT) { setTimedOut(true); clearInterval(timer); }
        return w + 1;
      });
      try {
        const r = await builder.forCampaign(id);
        if (live && r.reportId) navigate(`/reports/${encodeURIComponent(r.reportId)}`, { replace: true });
      } catch { /* a failed poll is not a failed generation; the next one will say. */ }
    }, 2000);
    return () => { live = false; clearInterval(timer); };
  }, [queued, timedOut, id, navigate]);

  async function askForReport() {
    if (!lookup?.reportSo) return;
    setAsking(true);
    setError(null);
    setTimedOut(false);
    setWaited(0);
    try {
      const r = await reports.generate({ reportSo: lookup.reportSo });
      setQueued({ reportId: r.reportId, duplicate: r.duplicate });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setAsking(false);
    }
  }

  if (error) return <div className="banner">{error}</div>;
  if (!lookup) return <div className="empty">Looking for this campaign’s report…</div>;

  if (queued && timedOut) {
    // The poll gave up. Generation fails fast when it fails, so two minutes with no report almost
    // always means the job FAILED (or no worker is consuming) — say so and offer a retry, rather than
    // implying it is still working.
    return (
      <div className="empty-state">
        <h2>This report didn’t finish generating</h2>
        <p>
          It was queued but nothing arrived after two minutes. Generation fails quickly when it fails,
          so the job most likely errored — a common cause is the campaign not being ready to report on.
        </p>
        <p className="muted">
          Check the generate worker’s logs for the reason (<code>pm2 logs pdmg-generate-worker</code>),
          then try again. If it keeps failing, the campaign may need attention before it can be reported.
        </p>
        <p>
          <button className="button primary" onClick={askForReport} disabled={asking}>
            {asking ? 'Retrying…' : 'Try again'}
          </button>{' '}
          <Link className="button" to={`/campaigns/${encodeURIComponent(lookup.campaignId)}`}>
            Back to the campaign
          </Link>
        </p>
      </div>
    );
  }

  if (queued) {
    return (
      <div className="empty-state">
        <h2>Generating…</h2>
        <p>
          {queued.duplicate
            ? 'This report was already queued. Waiting for it to finish.'
            : 'Queued. The report is being built from this campaign’s delivery data.'}
        </p>
        <p className="muted">
          It appears here on its own. Generation reads every day of delivery in the window and
          renders a PDF, so it takes longer than a page load.
        </p>
        {/*
          * Said plainly after 30 seconds rather than spinning forever. The commonest cause by
          * far is that no generate worker is consuming the queue, and that is a thing someone
          * can act on — unlike a spinner.
          */}
        {waited > 15 && (
          <p className="banner">
            Still waiting. If nothing arrives, check that the generate worker is running:{' '}
            <code>pm2 logs pdmg-generate-worker</code>
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="empty-state">
      <h2>No report yet</h2>
      <p>
        Nothing has been generated for{' '}
        <strong>{lookup.campaignName || lookup.reportSo || 'this campaign'}</strong>
        {lookup.reportSo && <> (<code>{lookup.reportSo}</code>)</>}.
      </p>
      {lookup.reportSo ? (
        <>
          <p className="muted">
            This builds a post-flight report over the campaign’s full flight, from the
            delivery already collected. Every campaign under{' '}
            <code>{lookup.reportSo}</code> is included — a report covers the SO, not one campaign.
          </p>

          {/*
            * A multi-channel buy becomes one report with a section per channel — said here so it is
            * expected, not a surprise. The channels come from the ads that delivered, not a stored
            * flag (which is empty in this data).
            */}
          {lookup.generation?.composite && (
            <p className="muted">
              This SO delivered on more than one channel, so the report is{' '}
              <strong>composed</strong> — an overall summary followed by a section for each channel.
            </p>
          )}

          {/*
            * The one thing that actually stops a report being generated, said before the click.
            *
            * Generation reads from the consolidated store and refuses a window it has no delivery
            * for — a report of zeros is indistinguishable from a campaign that did not deliver. The
            * server resolves that same answer here, so a window with no data explains itself rather
            * than queueing a job that fails into a spinner. `generation === null` means the server
            * could not probe (generation unconfigured); the button is offered unchanged then.
            */}
          {lookup.generation?.blocked && (
            <div className="banner">{lookup.generation.blocked}</div>
          )}
          {/*
            * A channel with a template but no build path yet (DOOH, email). Distinct from an
            * empty window on purpose: no consolidation sweep will make this ready, so the message
            * says "not available yet", not "no delivery" — and the button is removed, not merely
            * disabled, because there is nothing to wait for.
            */}
          {lookup.generation?.unsupportedChannel && (
            <div className="banner">
              <strong>{lookup.generation.unsupportedChannel.toUpperCase()} reports aren’t available yet.</strong>{' '}
              This SO is a <code>{lookup.generation.unsupportedChannel}</code> campaign. The report
              template exists, but on-demand generation currently builds display and audio reports
              only — {lookup.generation.unsupportedChannel} is not wired up yet.
            </div>
          )}
          {lookup.generation && !lookup.generation.blocked
            && !lookup.generation.unsupportedChannel && !lookup.generation.ready && (
            <div className="banner banner-warn">
              <strong>No consolidated display delivery for this window.</strong>{' '}
              The report would cover{' '}
              <strong>{lookup.generation.window.start} → {lookup.generation.window.end}</strong>,
              and the store has no display delivery to build it from.{' '}
              {/*
                * Two different reasons, said apart because the fix differs. A window newer than
                * the store's last consolidated day resolves itself once the sweep catches up. A
                * window INSIDE coverage with no rows does not — it is an audio/DOOH-only buy, or a
                * campaign that genuinely did not deliver on display, and waiting changes nothing.
                */}
              {!lookup.generation.coverage
                ? <>The delivery store is empty — nothing has been consolidated to report from yet.</>
                : lookup.generation.window.start > lookup.generation.coverage.end
                  ? <>Delivery is consolidated through{' '}
                      <strong>{lookup.generation.coverage.end}</strong>, and this flight is newer —
                      it becomes ready once the next consolidation sweep reaches these days.</>
                  : <>The store covers{' '}
                      <strong>{lookup.generation.coverage.start} → {lookup.generation.coverage.end}</strong>,
                      so this window is inside it: the campaign delivered on another channel
                      (audio or DOOH) or did not deliver on display. Waiting will not change that.</>}
              {' '}Generating now would produce a report of zeros, so it is held.
            </div>
          )}

          <p>
            {/*
              * When the store has no delivery, the button is disabled rather than removed: the
              * campaign is real and the report will exist once its delivery consolidates, and a
              * disabled control that says why reads better than a missing one. `blocked` removes it
              * outright — there is nothing to generate against at all.
              */}
            {!lookup.generation?.blocked && !lookup.generation?.unsupportedChannel && (
              <button
                className="button primary"
                onClick={askForReport}
                disabled={asking || (lookup.generation ? !lookup.generation.ready : false)}
                title={lookup.generation && !lookup.generation.ready
                  ? 'No consolidated delivery for this window yet'
                  : undefined}
              >
                {asking ? 'Requesting…' : 'Generate report'}
              </button>
            )}{' '}
            <Link className="button" to={`/campaigns/${encodeURIComponent(lookup.campaignId)}`}>
              Back to the campaign
            </Link>
          </p>
        </>
      ) : (
        <>
          {/*
            * Without an SO there is nothing to group a report by, and generation would have
            * nothing to select. Said here rather than offering a button that refuses.
            */}
          <p className="muted">
            This campaign has no SO, so there is nothing to generate a report against.
          </p>
          <p>
            <Link className="button" to={`/campaigns/${encodeURIComponent(lookup.campaignId)}`}>
              Back to the campaign
            </Link>
          </p>
        </>
      )}
    </div>
  );
}

/**
 * A static, shareable URL keyed on SO + window (`/reports/so/:reportSo/:start/:end`).
 *
 * A report's window is part of its id, so a date change makes a new id and any old id URL 404s. This URL
 * does not: it asks generation for the report over the stated window (idempotent — dedups to the existing
 * one, else builds it, consolidating today on demand), waits for the document, then forwards to the
 * canonical `/reports/:reportId`. Bookmark THIS and it always resolves.
 */
export function WindowResolverScreen() {
  const { reportSo = '', start = '', end = '' } = useParams();
  const navigate = useNavigate();
  // Carry any presentation query params (geographic_targeting=Y, theme_color=…, etc.) through to the
  // resolved report URL, so opening a shareable window URL applies them on the report it forwards to.
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  const [error, setError] = React.useState<string | null>(null);
  const [waited, setWaited] = React.useState(0);
  const [timedOut, setTimedOut] = React.useState(false);

  // SO-only URL (no dates) → let the server resolve the campaign's flight window from its ad groups.
  const window = start && end ? `${start} → ${end}` : 'its full flight';

  React.useEffect(() => {
    let live = true;
    (async () => {
      let reportId: string;
      try {
        const r = await reports.generate(
          start && end
            ? { reportSo, periodStart: start, periodEnd: end, lifecycle: 'POSTFLIGHT' }
            : { reportSo, lifecycle: 'POSTFLIGHT' }, // no window → resolveGeneration uses the flight dates
        );
        reportId = r.reportId;
      } catch (e) {
        if (live) setError(e instanceof ApiError ? e.message : String(e));
        return;
      }
      for (let i = 0; i < POLL_LIMIT && live; i++) {
        try {
          await builder.load(reportId);
          if (live) navigate(`/reports/${encodeURIComponent(reportId)}${search ? `?${search}` : ''}`, { replace: true });
          return;
        } catch { /* the worker has not written the document yet — keep polling */ }
        setWaited((w) => w + 1);
        await new Promise((res) => setTimeout(res, 2000));
      }
      if (live) setTimedOut(true);
    })();
    return () => { live = false; };
  }, [reportSo, start, end, navigate, search]);

  if (error) {
    return <div className="banner">Could not generate the report for {reportSo} ({window}): {error}</div>;
  }
  if (timedOut) {
    return (
      <div className="empty-state">
        <h2>This report didn’t finish generating</h2>
        <p>It was queued for <strong>{reportSo}</strong> ({window}) but nothing arrived after two minutes.</p>
        <p className="muted">Check the generate worker’s logs (<code>pm2 logs pdmg-generate-worker</code>), then reload.</p>
      </div>
    );
  }
  return (
    <div className="empty-state">
      <h2>Preparing the report…</h2>
      <p>Building <strong>{reportSo}</strong> for <strong>{window}</strong> from the campaign’s delivery data.</p>
      <p className="muted">
        It opens on its own. A window that reaches today is consolidated on demand, so it can take a moment.
      </p>
      {waited > 15 && (
        <p className="banner">Still working — if nothing arrives, check <code>pm2 logs pdmg-generate-worker</code>.</p>
      )}
    </div>
  );
}

export function ReportBuilderScreen() {
  const { reportId = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Which (reportId + query string) combination we've already applied, so URL-driven presentation
  // params are applied once per load rather than on every re-render.
  const appliedParamsRef = React.useRef<string>('');
  const [view, setView] = React.useState<BuilderView | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const { confirm, confirmDialog } = useConfirm();
  const [editing, setEditing] = React.useState(true);
  const [drawer, setDrawer] = React.useState(true);
  const zoom = 1; // fixed at 100% — the zoom control was removed
  const [picked, setPicked] = React.useState<Picked | null>(null);
  const [templates, setTemplates] = React.useState<
    Array<{ id: string; name: string; version: number; channels: string[]; source: 'built-in' | 'custom'; status?: string; v2?: boolean }>
  >([]);
  // The editable report window (rebuild-for-window). Seeded from the loaded report; changing it and
  // rebuilding opens the report for the new window (a new id) rather than relabelling this one.
  const [winStart, setWinStart] = React.useState('');
  const [winEnd, setWinEnd] = React.useState('');
  React.useEffect(() => {
    if (view) { setWinStart(view.periodStart); setWinEnd(view.periodEnd); }
  }, [view?.periodStart, view?.periodEnd]);

  React.useEffect(() => {
    let live = true;
    builder.load(reportId)
      .then((v) => { if (live) { setView(v); setError(null); } })
      .catch(async (e) => {
        // A 404 usually means a stale id — the campaign's window (and so its report id) moved. Rather than
        // dead-end, resolve the id to the CURRENT report for the same SO and forward there.
        if (live && e instanceof ApiError && e.status === 404) {
          try {
            const r = await builder.resolveStale(reportId);
            if (live && r.reportId && r.reportId !== reportId) {
              navigate(`/reports/${encodeURIComponent(r.reportId)}`, { replace: true });
              return;
            }
          } catch { /* fall through to the error below */ }
        }
        if (live) setError(e instanceof ApiError ? e.message : String(e));
      });
    return () => { live = false; };
  }, [reportId, navigate]);

  // The templates this report can be re-rendered with — loaded once for the picker.
  React.useEffect(() => {
    let live = true;
    builder.templates().then((t) => { if (live) setTemplates(t); }).catch(() => { /* picker just stays empty */ });
    return () => { live = false; };
  }, []);

  /*
   * Presentation query params, applied once per load.
   *
   * A shareable report URL can carry `geographic_targeting=Y` / `audience_targeting=Y` /
   * `top_sites=Y` (Y shows the section, anything else hides it) and `theme_color=rrggbb` (6 hex
   * digits, no `#`). Opening such a URL applies them as ordinary PRESENTATION overrides — so the
   * checkboxes in the drawer reflect them and the change persists to the PDF — but only when the
   * value actually differs, and never on an already-frozen report (the edit simply fails and is
   * ignored). Absent params are left untouched, so they are instructions, not a reset.
   */
  React.useEffect(() => {
    if (!view) return;
    const sig = `${reportId}?${searchParams.toString()}`;
    if (appliedParamsRef.current === sig) return;
    appliedParamsRef.current = sig; // mark handled up front so applying edits can't re-enter

    const desired: Array<{ path: string; value: string | boolean }> = [];
    const wantValue = (path: string, value: boolean) => {
      const current = view.settings.find((f) => f.path === path)?.value === true;
      if (current !== value) desired.push({ path, value });
    };
    const wantBool = (param: string, path: string) => {
      const raw = searchParams.get(param);
      if (raw === null) return; // absent → leave the stored value
      wantValue(path, raw.trim().toUpperCase() === 'Y');
    };

    /*
     * `sub_type` is a preset that turns the targeting sections on together, so a shareable report URL need
     * not name each one. When present it is AUTHORITATIVE over all three — a recognised value selects its
     * combination; a blank or unrecognised value shows none ("do not show any of the additional features").
     * When `sub_type` is absent, the individual flags below apply instead (and a fresh report is off by
     * default, which is also "none"). Works on every report type, since every one uses these same flags.
     */
    const SUB_TYPE_PRESETS: Record<string, { geo: boolean; topSites: boolean; audience: boolean }> = {
      map_only:                { geo: true, topSites: false, audience: false },
      map_targeting_top_sites: { geo: true, topSites: true,  audience: true  },
      map_top_sites:           { geo: true, topSites: true,  audience: false },
    };
    const subType = searchParams.get('sub_type');
    if (subType !== null) {
      const p = SUB_TYPE_PRESETS[subType.trim().toLowerCase()] ?? { geo: false, topSites: false, audience: false };
      wantValue('presentation.showGeoTargeting', p.geo);
      wantValue('presentation.showTopSites', p.topSites);
      wantValue('presentation.showAudienceTargeting', p.audience);
    } else {
      wantBool('geographic_targeting', 'presentation.showGeoTargeting');
      wantBool('audience_targeting', 'presentation.showAudienceTargeting');
      wantBool('top_sites', 'presentation.showTopSites');
    }

    const themeRaw = searchParams.get('theme_color');
    if (themeRaw && /^[0-9a-fA-F]{6}$/.test(themeRaw.trim())) {
      const hex = `#${themeRaw.trim().toLowerCase()}`;
      const field = view.settings.find((f) => f.path === 'presentation.themeColor');
      const current = typeof field?.value === 'string' ? field.value.toLowerCase() : '';
      if (current !== hex) desired.push({ path: 'presentation.themeColor', value: hex });
    }

    if (desired.length === 0) return;
    let live = true;
    (async () => {
      let latest: BuilderView | null = null;
      for (const d of desired) {
        try {
          latest = await builder.edit(reportId, {
            path: d.path, value: d.value, kind: 'PRESENTATION', reason: 'Set from the report URL',
          });
        } catch { /* report may be frozen (approved/delivered) — the URL can't change it */ }
      }
      if (live && latest) setView(latest);
    })();
    return () => { live = false; };
  }, [view, reportId, searchParams]);

  async function changeTemplate(templateId: string, templateVersion: number) {
    setBusy(true);
    try {
      setView(await builder.setTemplate(reportId, templateId, templateVersion));
      setPicked(null);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /*
   * Rebuild: regenerate + re-render in place.
   *
   * Rebuild is "regenerate then swap" (the OLD document survives until the new one is built), so
   * `load` keeps returning the old document throughout — polling until it merely LOADS would grab the
   * stale version and stop. So we capture the current `generatedAt` and wait until it CHANGES: that is
   * the fresh document. The window is generous because an on-demand rebuild also re-consolidates the
   * recent days from the source DB, which takes longer than a plain re-render.
   */
  const [rebuilding, setRebuilding] = React.useState(false);
  async function rebuild() {
    if (!(await confirm({
      title: 'Rebuild this report?',
      message: 'It regenerates from the latest campaign data and re-renders. '
        + 'Your section toggles and figure edits are kept.',
      confirmLabel: 'Rebuild',
    }))) return;
    setRebuilding(true);
    setError(null);
    const before = view?.generatedAt ?? '';
    try {
      await builder.rebuild(reportId);
      for (let attempt = 0; attempt < POLL_LIMIT; attempt++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const v = await builder.load(reportId);
          if (v.generatedAt && v.generatedAt !== before) { // the NEW document, not the pre-rebuild one
            setView(v);
            setRebuilding(false);
            return;
          }
        } catch { /* transient — keep polling */ }
      }
      setError('Rebuild is taking longer than expected; reload the page in a moment.');
      setRebuilding(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setRebuilding(false);
    }
  }

  /*
   * Rebuild for the (possibly edited) window from the toolbar's date control.
   *
   * Unchanged window → an in-place rebuild (regenerate + re-render, keeping edits). Changed window →
   * forward to the static SO+window URL, which builds/opens the report for that window (a different id,
   * since the window is part of the id) — no 404, and it consolidates today on demand. A window that
   * reaches today comes back provisional.
   */
  function rebuildForWindow() {
    if (!view) return;
    if (winStart === view.periodStart && winEnd === view.periodEnd) { void rebuild(); return; }
    if (!winStart || !winEnd || winEnd < winStart) {
      setError('Enter a valid date range (end on or after start) before rebuilding.');
      return;
    }
    navigate(`/reports/so/${encodeURIComponent(view.reportSo)}/${winStart}/${winEnd}`);
  }

  /*
   * Generate a SEPARATE report for the stated (edited) window.
   *
   * A report's window is part of its identity (the id encodes it), so "cover a different window"
   * cannot mean editing this report — it means a NEW report. This asks the generate worker for one
   * over the stated dates, which re-aggregates the figures for that window, then follows to it. This
   * report is untouched. Generation is async, so it polls the new id until the document exists
   * before navigating, rather than landing on a "not found" while the worker is still building.
   */
  const [generating, setGenerating] = React.useState(false);
  async function generateForWindow() {
    if (!view?.periodDrift) return;
    const { start, end } = view.periodDrift.stated;
    const g = view.periodDrift.generated;
    if (!(await confirm({
      title: `Generate a new report for ${start} → ${end}?`,
      message: `It re-aggregates the figures for that window as a separate report and opens it. `
        + `This report (covering ${g.start} → ${g.end}) is left unchanged.`,
      confirmLabel: 'Generate',
    }))) return;
    setGenerating(true);
    setError(null);
    try {
      const r = await reports.generate({
        reportSo: view.reportSo, periodStart: start, periodEnd: end, lifecycle: view.lifecycle,
      });
      for (let attempt = 0; attempt < 20; attempt++) {
        await new Promise((res) => setTimeout(res, 1500));
        try {
          await builder.load(r.reportId);
          // React Router reuses THIS component across the :reportId change (no remount), so the
          // transient state must be cleared by hand or the destination shows a stuck "Generating…".
          setGenerating(false);
          navigate(`/reports/${encodeURIComponent(r.reportId)}`);
          return;
        } catch { /* the worker has not written the document yet — keep polling */ }
      }
      // It was queued but is slow; go there anyway and let the screen show its own state.
      setGenerating(false);
      navigate(`/reports/${encodeURIComponent(r.reportId)}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setGenerating(false);
    }
  }


  /** Trigger a browser download of the report's PDF (session cookie rides along on the same-origin GET). */
  function downloadPdf() {
    const a = document.createElement('a');
    a.href = builder.pdfUrl(reportId);
    a.download = `${view?.reportSo || reportId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  /** Mark the report final (campaign complete) or reopen it — gates the Approve button. */
  async function markComplete(final: boolean) {
    setBusy(true);
    setError(null);
    try {
      setView(await builder.finalize(reportId, final));
      setPicked(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Approve a final report and hand over the PDF.
   *
   * "Start review" was removed from the toolbar, so this advances READY_FOR_REVIEW / CHANGES_REQUESTED
   * up to IN_REVIEW itself before approving (the lifecycle still only approves from IN_REVIEW), then
   * downloads the frozen PDF — the copy the client gets.
   */
  async function approveFinal() {
    if (!(await confirm({
      title: 'Approve this report?',
      message: 'Your name is recorded as the approver, and once approved the report is '
        + 'released for delivery to the client. This cannot be undone.',
      confirmLabel: 'Approve',
      tone: 'danger',
    }))) return;
    setBusy(true);
    setError(null);
    try {
      if (view?.status === 'READY_FOR_REVIEW' || view?.status === 'CHANGES_REQUESTED') {
        await builder.transition(reportId, 'IN_REVIEW');
      }
      setView(await builder.approve(reportId));
      setPicked(null);
      downloadPdf();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const changed = React.useMemo(() => new Set(view?.changed ?? []), [view]);

  /*
   * The popover is anchored to a value's position on screen, so it has to go when that
   * position stops being true. Repositioning on every scroll frame instead would fight
   * the user for control of a text input they are typing into.
   */
  React.useEffect(() => {
    if (!picked) return;
    const close = () => setPicked(null);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [picked]);

  const onPick = React.useCallback((path: string, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    // A heatmap cell opens the 5-level slider instead of the text popover.
    const heat = el.dataset.editKind === 'heat';
    setPicked({
      path,
      current: (el.textContent ?? '').trim(),
      // The type the field already holds decides the input's rules. The server coerces
      // as well — this is the affordance, not the guarantee.
      numeric: /^[\d,.\s%]+$/.test((el.textContent ?? '').trim()),
      // Viewport coordinates, paired with `position: fixed`. Document coordinates plus
      // `position: absolute` grew the page by the popover's height and left the view
      // scrolled somewhere else once it closed.
      top: r.bottom + 6,
      left: r.left,
      ...(heat ? { heat: true, heatMax: Number(el.dataset.editMax) || 0, heatValue: Number(el.dataset.editValue) || 0 } : {}),
    });
  }, []);

  // Remove a CTV app/channel or geo-target row; its impressions redistribute across the survivors.
  const onRemove = React.useCallback(async (dimension: string, key: string) => {
    if (dimension !== 'apps' && dimension !== 'geoTargets') return;
    setBusy(true);
    try {
      setView(await builder.removeItem(reportId, dimension, key));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not remove the row.');
    } finally { setBusy(false); }
  }, [reportId]);

  // Clear & reset — revert every figure/attribution edit to the generated figures; keep presentation.
  const onClear = React.useCallback(async () => {
    if (!(await confirm({
      title: 'Clear all figure and attribution edits?',
      message: 'Inline changes, Desired Impressions, removed apps and geo targets are reverted to the '
        + 'generated figures.\n\nSection toggles, colors and client/agency names are kept.',
      confirmLabel: 'Clear edits',
      tone: 'danger',
    }))) return;
    setBusy(true);
    try {
      setView(await builder.clear(reportId));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not clear the report.');
    } finally { setBusy(false); }
  }, [reportId, confirm]);

  async function commit(value: string, kind: OverrideKind, reason: string) {
    if (!picked) return;
    setBusy(true);
    try {
      const next = await builder.edit(reportId, {
        path: picked.path,
        value,
        kind,
        reason: reason.trim() === '' ? undefined : reason.trim(),
      });
      setView(next);
      setPicked(null);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /*
   * A settings change is an override like any other.
   *
   * PRESENTATION is the right kind — it changes how the report is shown, not what was
   * measured — and D16 makes a reason mandatory for that kind, so one is supplied rather
   * than prompting for it on every checkbox. The field's own label is the honest reason:
   * it says what was changed, which is what a reviewer reads.
   */
  async function setSetting(path: string, value: string | boolean) {
    const field = view?.settings.find((f) => f.path === path);
    setBusy(true);
    try {
      setView(await builder.edit(reportId, {
        path,
        value,
        kind: 'PRESENTATION',
        reason: `${field?.label ?? path} changed in report settings`,
      }));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function revert(path: string) {
    setBusy(true);
    try {
      setView(await builder.revert(reportId, path));
      setPicked(null);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (error && !view) return <div className="banner">{error}</div>;
  if (!view) return <div className="empty">Loading the report…</div>;

  /*
   * Only offer templates for THIS report's channel — an eblast report must not list display
   * templates, and vice versa. The report's channel comes from its current template's declared
   * channels (reliable, server-declared) — falling back to inferring from the template id when the
   * exact version is not in the list — plus `campaign.channels` (email reports carry ['EMAIL'];
   * display reports carry none, since the worker derives channels from ads.type). A template shows
   * when it shares a channel with the report. The current template always remains selectable.
   */
  const inferChannels = (id: string): string[] => {
    const s = id.toLowerCase();
    if (s.includes('email') || s.includes('eblast')) return ['EMAIL'];
    if (s.includes('audio')) return ['AUDIO'];
    if (s.includes('dooh')) return ['DOOH'];
    if (s.includes('composite')) return ['DISPLAY', 'AUDIO', 'EMAIL', 'DOOH'];
    return ['DISPLAY'];
  };
  const currentTpl = templates.find((t) => t.id === view.templateId);
  const reportChannels = new Set(
    [...(currentTpl?.channels ?? inferChannels(view.templateId)), ...(view.channels ?? [])]
      .map((c) => c.toUpperCase()),
  );
  // Only the V2 templates are offered. A report still on an older template keeps it listed (by name), so
  // the picker never blanks — and a channel with no V2 template yet simply shows its current one.
  const isCurrent = (t: { id: string; version: number }) => t.id === view.templateId && t.version === view.templateVersion;
  const offered = templates.filter((t) => t.v2 || isCurrent(t));
  const visibleTemplates = reportChannels.size === 0
    ? offered
    : offered.filter((t) => isCurrent(t) || (t.channels ?? []).some((c) => reportChannels.has(c.toUpperCase())));

  return (
    <div className={`builder${drawer ? '' : ' builder-wide'}`}>
      {confirmDialog}
      {/* Progress indicator while a rebuild runs — regeneration + re-render is asynchronous, so a
          persistent spinner tells the reviewer it is working rather than stuck. */}
      {rebuilding && (
        <div className="report-canvas-status" role="status" aria-live="polite">
          <svg className="spin" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="3"
              strokeLinecap="round" strokeDasharray="40 100">
              <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12"
                dur="0.8s" repeatCount="indefinite" />
            </circle>
          </svg>
          Rebuilding report… retrieving delivery and re-rendering
        </div>
      )}
      <div className="builder-bar">
        <div className="builder-id">
          <strong>{view.reportSo}</strong>
          <span className="pill">{view.status}</span>
          {/* Which metric rules produced these figures. A v1 report reproduces the
              legacy engine bug-for-bug; a v2 one does not, and a reviewer comparing
              against an older report needs to know which they are looking at. */}
          <span className="pill">{view.metricsVersion}</span>
        </div>
        <div className="builder-actions">
          {/*
            * Complete → Approve. A report is not approved while its campaign is still running: first mark
            * it complete (a per-report flag), which then reveals Approve. Approving downloads the final PDF.
            * A frozen (APPROVED/DELIVERED) report shows neither.
            */}
          {view.status !== 'APPROVED' && view.status !== 'DELIVERED' && (
            view.final ? (
              <>
                <button className="button primary" onClick={approveFinal} disabled={busy || rebuilding}>Approve</button>
                <button onClick={() => markComplete(false)} disabled={busy || rebuilding}
                  title="Reopen — the report is not yet complete">Reopen</button>
              </>
            ) : (
              <button onClick={() => markComplete(true)} disabled={busy || rebuilding}
                title="Mark this report complete (campaign finished) to enable approval">
                Mark complete
              </button>
            )
          )}
          {/*
            * Re-template a report in review. The preview re-renders in the chosen template
            * immediately; the figures do not change. A delivered report's picker is disabled — its
            * template is pinned to what the client received.
            */}
          <label className="template-pick" title="Template this report is rendered with">
            <span className="setting-label tiny muted">Template</span>
            <select
              value={`${view.templateId}:${view.templateVersion}`}
              disabled={busy || view.status === 'DELIVERED' || templates.length === 0}
              onChange={(e) => {
                const [id, v] = e.target.value.split(':');
                if (id !== view.templateId || Number(v) !== view.templateVersion) changeTemplate(id, Number(v));
              }}
            >
              {/* The current template is always an option even if it is not in the channel-filtered
                  list (e.g. an older version), so the select never shows a blank. */}
              {!visibleTemplates.some((t) => t.id === view.templateId && t.version === view.templateVersion) && (
                <option value={`${view.templateId}:${view.templateVersion}`}>{view.templateId} v{view.templateVersion}</option>
              )}
              {visibleTemplates.map((t) => (
                <option key={`${t.id}:${t.version}`} value={`${t.id}:${t.version}`}>
                  {t.name}{t.source === 'custom' ? ` (custom${t.status && t.status !== 'published' ? `, ${t.status}` : ''})` : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="toggle">
            <input type="checkbox" checked={editing} onChange={(e) => { setEditing(e.target.checked); setPicked(null); }} />
            Edit values
          </label>
          <a className="button" href={builder.pdfUrl(view.reportId)}>Download PDF</a>
          {/*
            * Report window + rebuild. Editing the dates and rebuilding retrieves the campaign's data FOR
            * that window (a window reaching today is consolidated on demand). An unchanged window rebuilds
            * this report in place; a changed one opens the report for the new window.
            */}
          {view.status !== 'DELIVERED' && view.status !== 'APPROVED' && (
            <span className="window-picker" title="The report window. Change it and rebuild to retrieve that window's data.">
              <input type="date" value={winStart} max={winEnd || undefined}
                onChange={(e) => setWinStart(e.target.value)} aria-label="Report window start" />
              <span className="muted">→</span>
              <input type="date" value={winEnd} min={winStart || undefined}
                onChange={(e) => setWinEnd(e.target.value)} aria-label="Report window end" />
            </span>
          )}
          <button
            onClick={rebuildForWindow}
            disabled={busy || rebuilding || generating || view.status === 'DELIVERED' || view.status === 'APPROVED'}
            title={view.status === 'DELIVERED' || view.status === 'APPROVED'
              ? 'A delivered or approved report cannot be rebuilt — regenerate a new version instead.'
              : (winStart !== view.periodStart || winEnd !== view.periodEnd)
                ? 'Retrieve the campaign’s data for this window (opens the report for it)'
                : 'Regenerate from the latest data and re-render, keeping your edits and toggles.'}
          >
            {rebuilding ? 'Rebuilding…'
              : (winStart !== view.periodStart || winEnd !== view.periodEnd) ? 'Rebuild for window' : 'Rebuild'}
          </button>
          <button onClick={() => setDrawer((d) => !d)}>{drawer ? 'Hide' : 'Settings'}</button>
        </div>
      </div>

      {error && <div className="banner">{error}</div>}
      {/*
        The window reaches today, so the figures were consolidated on demand and are still moving — the
        report re-settles as the day closes. Said plainly so a reviewer does not read provisional numbers
        as final; rebuild later for the settled figures.
      */}
      {view.provisionalAsOf && !view.final && (
        <div className="banner banner-warn">
          <strong>Provisional figures.</strong>{' '}
          This window includes today, so the delivery was consolidated on demand as of{' '}
          <strong>{new Date(view.provisionalAsOf).toLocaleString()}</strong> and will change as the day
          finishes. Rebuild once the campaign’s days have closed for the settled numbers.
        </div>
      )}
      {/*
        Louder than a changed-field marker, because this is the one edit that can make
        correct figures state something false. It names both windows rather than saying
        "check the dates", so the reader can see immediately which way it is wrong.
      */}
      {view.periodDrift && (
        <div className="banner banner-warn">
          <strong>The stated period no longer matches the figures.</strong>{' '}
          These figures were aggregated for{' '}
          <strong>{view.periodDrift.generated.start} → {view.periodDrift.generated.end}</strong>,
          and the report now states{' '}
          <strong>{view.periodDrift.stated.start} → {view.periodDrift.stated.end}</strong>.{' '}
          Editing the period only <strong>relabels</strong> this report — it does not re-aggregate,
          and <strong>Rebuild keeps this report’s own window</strong> (the window is part of the
          report’s identity). To get figures for the stated window, generate a separate report for
          it, or revert the dates in the settings panel.
          <div className="banner-actions">
            <button
              className="button primary"
              onClick={generateForWindow}
              disabled={busy || generating || rebuilding}
              title="Creates a new report for the stated window and opens it; this report is unchanged"
            >
              {generating
                ? 'Generating…'
                : `Generate a report for ${view.periodDrift.stated.start} → ${view.periodDrift.stated.end}`}
            </button>
          </div>
        </div>
      )}
      {view.violations.length > 0 && (
        <div className="banner">
          {/* An unsatisfiable invariant is shown, never swallowed: it means the figures on
              the page contradict each other and the report must not go out that way. */}
          <strong>This report does not balance.</strong> {view.violations.join(' ')}
        </div>
      )}

      <div className="builder-body">
        <div className="builder-stage">
          <ReportCanvas doc={view.document} reportId={reportId} changed={changed} reconcile={view.reconcile} editing={editing} onPick={onPick} onRemove={onRemove} zoom={zoom} />
        </div>

        {drawer && (
          <aside className="builder-drawer">
            {/* Simulated vs actual impressions is a DISPLAY-delivery concept (is_sim on display_delivery).
                An EMAIL report measures recipients, not impressions — there is no sim path — so the panel is
                hidden there; it was offering a meaningless Simulated/Actual 0/0 with a reconcile on eblast.
                Gated on the absence of EMAIL (not the presence of DISPLAY) because `campaign.channels` is
                empty on real display reports — the worker derives delivery channels from ads.type — while an
                email report reliably carries ['EMAIL']. */}
            {!(view.channels ?? []).includes('EMAIL') && <ImpressionsPanel reportId={reportId} />}
            <SettingsPanel view={view} onSet={setSetting} busy={busy} />
            <OverridePanel view={view} onRevert={revert} busy={busy} />
            <section className="drawer-section">
              <button className="button danger" disabled={busy} onClick={onClear}>Clear &amp; reset</button>
              <p className="setting-help tiny muted">
                Revert every figure &amp; attribution edit to the generated report. Keeps section toggles,
                colors and client/agency names.
              </p>
            </section>
          </aside>
        )}
      </div>

      {picked && editing && (picked.heat ? (
        <HeatSlider
          key={picked.path}
          picked={picked}
          busy={busy}
          alreadyChanged={changed.has(picked.path)}
          onCancel={() => setPicked(null)}
          onCommit={commit}
          onRevert={() => revert(picked.path)}
        />
      ) : (
        <EditPopover
          key={picked.path}
          picked={picked}
          busy={busy}
          alreadyChanged={changed.has(picked.path)}
          onCancel={() => setPicked(null)}
          onCommit={commit}
          onRevert={() => revert(picked.path)}
        />
      ))}
    </div>
  );
}

/**
 * A five-position heat slider for one heatmap cell.
 *
 * The heatmap shades each cell by its value against the busiest cell, so "heat level" 0–4 maps to
 * 0 / 25 / 50 / 75 / 100% of that max — level 4 is the busiest-cell shade, never brighter, so setting
 * a cell cannot rescale the whole map. It commits a PRESENTATION override on the cell's value path
 * (the `video` channel has no invariants, so only that cell moves). A reason is auto-supplied.
 */
function HeatSlider(
  { picked, busy, alreadyChanged, onCancel, onCommit, onRevert }: {
    picked: Picked; busy: boolean; alreadyChanged: boolean;
    onCancel: () => void;
    onCommit: (value: string, kind: OverrideKind, reason: string) => void;
    onRevert: () => void;
  },
) {
  const max = picked.heatMax && picked.heatMax > 0 ? picked.heatMax : 1;
  const levelOf = (v: number) => Math.max(0, Math.min(4, Math.round((v / max) * 4)));
  const [level, setLevel] = React.useState(levelOf(picked.heatValue ?? 0));
  const valueForLevel = (l: number) => Math.round((l / 4) * max);
  const LABELS = ['None', 'Low', 'Medium', 'High', 'Max'];

  return (
    <div
      className="edit-pop heat-pop"
      style={{ top: picked.top, left: Math.min(picked.left, Math.max(8, window.innerWidth - 220)) }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') { e.stopPropagation(); onCancel(); }
        if (e.key === 'Enter' && !busy) { e.preventDefault(); onCommit(String(valueForLevel(level)), 'PRESENTATION', `heatmap cell set to ${LABELS[level].toLowerCase()}`); }
      }}
    >
      <div className="edit-pop-path">Heat level</div>
      <input
        type="range" min={0} max={4} step={1} value={level}
        onChange={(e) => setLevel(Number(e.target.value))}
        aria-label="Heat level" style={{ width: '100%' }}
        autoFocus
      />
      <div className="tiny muted" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>None</span><span><strong>{LABELS[level]}</strong></span><span>Max</span>
      </div>
      <div className="edit-pop-actions">
        <button className="primary" disabled={busy}
          onClick={() => onCommit(String(valueForLevel(level)), 'PRESENTATION', `heatmap cell set to ${LABELS[level].toLowerCase()}`)}>
          {busy ? 'Saving…' : 'Apply'}
        </button>
        {alreadyChanged && <button disabled={busy} onClick={onRevert}>Revert</button>}
        <button onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * Simulated vs actual impressions, and the reconcile control.
 *
 * The report shows the SIMULATED (never-zero) impressions by default; this panel puts the measured figure
 * beside it so staff can compare, and reconcile a report to actual on demand. Reconciling rebuilds, which is
 * what freezes the chosen figures when the report is approved/exported. Clicks are identical in both modes.
 */
function ImpressionsPanel({ reportId }: { reportId: string }): JSX.Element {
  const [cmp, setCmp] = React.useState<ImpressionComparison | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [working, setWorking] = React.useState(false);

  const load = React.useCallback(() => {
    builder.impressionComparison(reportId)
      .then(setCmp)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load impressions.'));
  }, [reportId]);
  React.useEffect(() => { load(); }, [load]);

  async function reconcile(mode: 'sim' | 'actual') {
    setError(null); setWorking(true);
    try {
      await builder.reconcile(reportId, mode);
      // Reconcile rebuilds asynchronously; reflect the new mode and let the page's own poll refresh the doc.
      setCmp((c) => (c ? { ...c, mode } : c));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Reconcile failed.');
    } finally { setWorking(false); }
  }

  const fmt = (n: number) => n.toLocaleString();
  return (
    <div className="drawer-panel impressions-panel">
      <h3>Impressions</h3>
      {error && <div className="banner error" style={{ marginBottom: '0.5rem' }}>{error}</div>}
      {!cmp ? <div className="busy">Loading…</div> : cmp.simApplies === false ? (
        // CTV / OTT and DOOH: measured buys with no simulated path — show the delivered figure and skip
        // the sim/reconcile controls, which do not apply.
        <>
          <table className="cmp-table">
            <tbody>
              <tr className="is-current">
                <td>Delivered ·</td>
                <td className="num">{cmp.channel === 'dooh' && cmp.weighted === null ? 'TBD' : fmt(cmp.actual.impressions)}</td>
              </tr>
            </tbody>
          </table>
          {cmp.channel === 'dooh' ? (
            <p className="hint">
              {cmp.weighted === null
                ? 'Pre-flight: no delivery yet. DOOH has no simulated figure.'
                : cmp.weighted
                  ? <>Audience-weighted impressions from real screen plays, the report&apos;s headline.
                      {(cmp.nonScreenImpressions ?? 0) > 0
                        ? <> Excludes {fmt(cmp.nonScreenImpressions!)} impressions the DOOH ads won on mobile / web — not screen plays.</>
                        : null} DOOH has no simulated figure.</>
                  : <><strong>Not weighted.</strong> No screen plays were recorded for this campaign, so this is the recorded delivery on its DOOH ads — not the audience-weighted figure. It becomes weighted once plays are recorded.</>}
            </p>
          ) : (
            <p className="hint">
              Delivered impressions (video starts), the report's headline. CTV has no simulated figure — to present a specific total, set{' '}
              <strong>Desired Impressions</strong> in Settings and the whole report scales to it.
            </p>
          )}
        </>
      ) : (
        <>
          <table className="cmp-table">
            <tbody>
              <tr className={cmp.mode === 'sim' ? 'is-current' : ''}>
                <td>Simulated{cmp.mode === 'sim' ? ' ·' : ''}</td>
                <td className="num">{fmt(cmp.simulated.impressions)}</td>
              </tr>
              <tr className={cmp.mode === 'actual' ? 'is-current' : ''}>
                <td>Actual{cmp.mode === 'actual' ? ' ·' : ''}</td>
                <td className="num">{fmt(cmp.actual.impressions)}</td>
              </tr>
            </tbody>
          </table>
          <p className="hint">
            This report shows the <strong>{cmp.mode === 'sim' ? 'simulated' : 'actual'}</strong> figure.
            Clicks are the same in both.
          </p>
          <div className="cmp-actions">
            <button className="button" disabled={working || cmp.mode === 'sim'} onClick={() => reconcile('sim')}>
              Use simulated
            </button>
            <button className="button" disabled={working || cmp.mode === 'actual'} onClick={() => reconcile('actual')}>
              Reconcile to actual
            </button>
          </div>
          {working && <p className="hint">Rebuilding with the new figures…</p>}
        </>
      )}
    </div>
  );
}

function SettingsPanel(
  { view, onSet, busy }: {
    view: BuilderView;
    onSet: (path: string, value: string | boolean) => void;
    busy: boolean;
  }
) {
  return (
    <>
      <section className="drawer-section">
        <h3>Settings</h3>
        {/*
          Every control writes an override on the path the schema declares, through the
          same endpoint as an in-line figure edit — so a settings change is appended,
          audited and reversible like any other, and shows up in Adjustments below.
        */}
        <div className="settings-form">
          {view.settings.map((f) => (
            <SettingControl
              key={f.path}
              field={f}
              changed={view.changed.includes(f.path)}
              busy={busy}
              onSet={(v) => onSet(f.path, v)}
            />
          ))}
        </div>
      </section>
    </>
  );
}

function SettingControl(
  { field, changed, busy, onSet }: {
    field: SettingsField;
    changed: boolean;
    busy: boolean;
    onSet: (value: string | boolean) => void;
  }
) {
  /*
   * Text and colour are held locally and committed on blur or Enter, which is the legacy
   * editor's contract and the only sane one here: every commit is a round trip that
   * re-renders three pages, so committing per keystroke would fight the typist.
   * A checkbox has no intermediate state, so it commits immediately.
   */
  const [draft, setDraft] = React.useState(field.value === null ? '' : String(field.value));
  React.useEffect(() => {
    setDraft(field.value === null ? '' : String(field.value));
  }, [field.value]);

  const commit = () => {
    const current = field.value === null ? '' : String(field.value);
    if (draft.trim() === current) return;   // nothing to record
    onSet(draft.trim());
  };

  if (field.kind === 'date') {
    /*
     * A native date input, committed on change rather than on blur.
     *
     * A date picker has no meaningful intermediate state — the value is either a whole
     * date or nothing — so waiting for blur just leaves the page disagreeing with the
     * control the user has already finished with.
     */
    return (
      <label className={`setting${changed ? ' setting-changed' : ''}`} title={field.help}>
        <span className="setting-label">{field.label}</span>
        <input
          type="date"
          value={typeof field.value === 'string' ? field.value : ''}
          disabled={busy}
          onChange={(e) => { if (e.target.value) onSet(e.target.value); }}
        />
        {field.help && <span className="setting-help tiny muted">{field.help}</span>}
      </label>
    );
  }

  if (field.kind === 'boolean') {
    return (
      <label className={`setting toggle${changed ? ' setting-changed' : ''}`} title={field.help}>
        <input
          type="checkbox"
          checked={field.value === true}
          disabled={busy}
          onChange={(e) => onSet(e.target.checked)}
        />
        <span>{field.label}</span>
      </label>
    );
  }

  return (
    <label className={`setting${changed ? ' setting-changed' : ''}`} title={field.help}>
      <span className="setting-label">{field.label}</span>
      <span className="setting-input">
        {field.kind === 'colour' && (
          // A native colour input alongside the text field: the swatch is how anyone
          // actually picks a brand colour, and the text field is how they paste one.
          <input
            type="color"
            value={/^#[0-9a-fA-F]{6}$/.test(draft) ? draft : '#001345'}
            disabled={busy}
            onChange={(e) => onSet(e.target.value)}
            aria-label={`${field.label} swatch`}
          />
        )}
        <input
          type={field.kind === 'integer' ? 'number' : 'text'}
          min={field.kind === 'integer' ? 0 : undefined}
          step={field.kind === 'integer' ? 1 : undefined}
          value={draft}
          disabled={busy}
          placeholder={field.kind === 'colour' ? 'template default'
            : field.kind === 'integer' ? 'measured figure' : ''}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            if (e.key === 'Escape') setDraft(field.value === null ? '' : String(field.value));
          }}
        />
      </span>
      {field.help && <span className="setting-help tiny muted">{field.help}</span>}
    </label>
  );
}

function OverridePanel(
  { view, onRevert, busy }: { view: BuilderView; onRevert: (p: string) => void; busy: boolean }
) {
  /*
   * The latest edit per path, not every edit ever made.
   *
   * The store is append-only, so a figure edited three times has three rows. Listing all
   * of them reads as three separate corrections; what a reviewer needs is where the
   * report stands now, with the history a click away.
   */
  const latest = new Map<string, BuilderView['overrides'][number]>();
  for (const o of view.overrides) latest.set(o.path, o);
  const active = [...latest.values()].filter((o) => view.changed.includes(o.path));

  return (
    <section className="drawer-section">
      <h3>Adjustments {active.length > 0 && <span className="pill">{active.length}</span>}</h3>
      {active.length === 0 ? (
        <p className="muted">No figure has been changed. Everything shown is what the engine measured.</p>
      ) : (
        <>
          <ul className="override-list">
            {active.map((o) => (
              <li key={o.path}>
                <div className="override-head">
                  <code>{o.path}</code>
                  <button disabled={busy} onClick={() => onRevert(o.path)}>Revert</button>
                </div>
                <div className="override-values">
                  <span className="was">{String(o.originalValue ?? '—')}</span>
                  <span className="arrow">→</span>
                  <strong>{String(o.value)}</strong>
                </div>
                <div className="tiny muted">
                  {o.kind}{o.reason ? ` · ${o.reason}` : ''} · {o.userName ?? o.userId} · {o.createdAt.slice(0, 10)}
                </div>
              </li>
            ))}
          </ul>
          {view.consequentialChanges > 0 && (
            <p className="tiny muted">
              {view.consequentialChanges} further {view.consequentialChanges === 1 ? 'figure was' : 'figures were'}
              {' '}adjusted by the engine to keep the report consistent.
            </p>
          )}
        </>
      )}
      <p className="tiny muted">
        Adjustments are appended, never applied over the original. What the engine measured
        is always recoverable. <Link to={`/admin/reports/${view.reportId}`}>Full history</Link>
      </p>
    </section>
  );
}

function EditPopover(
  { picked, busy, alreadyChanged, onCancel, onCommit, onRevert }: {
    picked: Picked; busy: boolean; alreadyChanged: boolean;
    onCancel: () => void;
    onCommit: (value: string, kind: OverrideKind, reason: string) => void;
    onRevert: () => void;
  }
) {
  const [value, setValue] = React.useState(picked.current);
  const [kind, setKind] = React.useState<OverrideKind>('DATA_GAP');
  const [reason, setReason] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);

  // D16: the kinds that assert something beyond measurement must say why. Enforced on
  // the server; disabling the button here just avoids a pointless round trip.
  const reasonRequired = kind !== 'DATA_GAP';
  const ready = value.trim() !== '' && (!reasonRequired || reason.trim() !== '');

  return (
    <div
      className="edit-pop"
      style={{
        top: picked.top,
        // Kept on screen when a value sits near the right edge of the page.
        left: Math.min(picked.left, Math.max(8, window.innerWidth - 296)),
      }}
      onKeyDown={(e) => {
        // Escape abandons, Enter commits — the legacy editor's keys.
        if (e.key === 'Escape') { e.stopPropagation(); onCancel(); }
        if (e.key === 'Enter' && !e.shiftKey && ready && !busy) { e.preventDefault(); onCommit(value, kind, reason); }
      }}
    >
      <div className="edit-pop-path"><code>{picked.path}</code></div>
      <input
        ref={inputRef}
        value={value}
        inputMode={picked.numeric ? 'decimal' : 'text'}
        onChange={(e) => setValue(e.target.value)}
        aria-label={`New value for ${picked.path}`}
      />
      <select value={kind} onChange={(e) => setKind(e.target.value as OverrideKind)} aria-label="Kind of adjustment">
        <option value="DATA_GAP">Data gap</option>
        <option value="CONTRACTUAL">Contractual</option>
        <option value="PRESENTATION">Presentation</option>
      </select>
      <p className="tiny muted">{KIND_HELP[kind]}</p>
      <input
        placeholder={reasonRequired ? 'Reason (required)' : 'Reason (optional)'}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        aria-label="Reason"
      />
      <div className="edit-pop-actions">
        <button className="primary" disabled={!ready || busy} onClick={() => onCommit(value, kind, reason)}>
          {busy ? 'Saving…' : 'Apply'}
        </button>
        {alreadyChanged && <button disabled={busy} onClick={onRevert}>Revert</button>}
        <button onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
